import {Injectable} from '@angular/core';

import {defer, forkJoin, Observable, of, throwError} from 'rxjs';
import {catchError, finalize, map, shareReplay, switchMap, take, tap} from 'rxjs/operators';

import {FileChangesSessionService, PendingFileChange} from './file-changes-session.service';

import {ProfileMode} from '../../app.constants';
import {ApiService} from '../api/';

export const DEFAULT_DELETE_FILE_URL = '/ga/api/v1/empFile/deleteEmpBinaryFile';

/**
 * Defers file uploads and deletions made in a form until that form is saved or cancelled.
 * Each form owns a {@link FileChangesSessionService}; this service applies the session changes on the server and
 * retries the deletions that failed.
 */
@Injectable({providedIn: 'root'})
export class FileChangesService {

  private readonly pendingFileCleanup = new Map<string, PendingFileChange>();
  private readonly fileCleanupRequests = new Map<string, Observable<void>>();
  private readonly trackedFileSessions = new WeakSet<FileChangesSessionService>();

  constructor(private api: ApiService) {
  }

  public deleteFile(fileId: string, url?: string, authenticated = true,
                    profileMode: ProfileMode = ProfileMode.CLASSIC): Observable<any> {
    return this.api.post(`${url || DEFAULT_DELETE_FILE_URL}/${fileId}?profile-mode=${profileMode}`, null,
      {
        requestOptions: {
          withCredentials: authenticated
        }
      });
  }

  public markFileForUpload(session: FileChangesSessionService, fileId: string | number, url?: string,
                           authenticated = true, profileMode: ProfileMode = ProfileMode.CLASSIC): void {
    const change = {fileId: `${fileId}`, url, authenticated, profileMode};
    this.discardOnSessionDestroy(session);
    session.uploads.set(this.fileChangeKey(change), change);
  }

  public markFileForDeletion(session: FileChangesSessionService, fileId: string | number, url?: string,
                             authenticated = true, profileMode: ProfileMode = ProfileMode.CLASSIC): Observable<void> {
    return defer(() => {
      const change = {fileId: `${fileId}`, url, authenticated, profileMode};
      const key = this.fileChangeKey(change);
      if (session.uploads.delete(key)) {
        this.pendingFileCleanup.set(key, change);
        return this.retryPendingFileCleanup();
      }
      session.deletions.set(key, change);
      return of(undefined);
    });
  }

  public discardPendingFileChanges(session: FileChangesSessionService): Observable<void> {
    return defer(() => {
      session.uploads.forEach((change, key) => this.pendingFileCleanup.set(key, change));
      session.uploads.clear();
      session.deletions.clear();
      return this.retryPendingFileCleanup();
    });
  }

  public commitPendingFileChanges(session: FileChangesSessionService): Observable<void> {
    return defer(() => {
      session.deletions.forEach((change, key) => this.pendingFileCleanup.set(key, change));
      session.uploads.clear();
      session.deletions.clear();
      return this.retryPendingFileCleanup();
    });
  }

  /**
   * Runs a save request that may reference uploaded files and commits the sessions once it succeeds.
   * Uploads are withdrawn from the sessions while the request is in flight, so a discard, a context switch or a
   * destroyed form can never delete a file that the server may already reference. They are restored only when the
   * save explicitly fails; an interrupted save leaves them untouched.
   */
  public saveWithPendingFileChanges<T>(sessions: FileChangesSessionService | FileChangesSessionService[],
                                       save$: Observable<T>): Observable<T> {
    return defer(() => {
      const tracked = ([] as FileChangesSessionService[]).concat(sessions).filter(Boolean);
      const inFlight = tracked.map(session => {
        const uploads = new Map(session.uploads);
        session.uploads.clear();
        return {session, uploads};
      });
      return save$.pipe(
        catchError(error => {
          inFlight.forEach(({session, uploads}) => uploads.forEach((change, key) => session.uploads.set(key, change)));
          return throwError(() => error);
        }),
        switchMap(result => tracked.length
          ? forkJoin(tracked.map(session => this.commitPendingFileChanges(session))).pipe(map(() => result))
          : of(result))
      );
    });
  }

  public retryPendingFileCleanup(): Observable<void> {
    return defer(() => {
      const requests = Array.from(this.pendingFileCleanup.entries()).map(([key, change]) => {
        if (!this.fileCleanupRequests.has(key)) {
          const request = defer(() => this.deleteFile(change.fileId, change.url, change.authenticated, change.profileMode)).pipe(
            tap(() => this.pendingFileCleanup.delete(key)),
            // The API displays errors. Retain failed cleanup without treating already-saved data as unsaved.
            catchError(error => {
              if (error?.status === 404) {
                this.pendingFileCleanup.delete(key);
              }
              return of(undefined);
            }),
            map(() => undefined),
            finalize(() => this.fileCleanupRequests.delete(key)),
            shareReplay({bufferSize: 1, refCount: false})
          );
          this.fileCleanupRequests.set(key, request);
        }
        return this.fileCleanupRequests.get(key);
      });
      return requests.length ? forkJoin(requests).pipe(map(() => undefined)) : of(undefined);
    });
  }

  private fileChangeKey(change: PendingFileChange): string {
    return JSON.stringify([change.url || DEFAULT_DELETE_FILE_URL, change.fileId, change.authenticated, change.profileMode]);
  }

  // A form closed without save or cancel (modal cross, ESC, navigation) must not leave orphan uploads.
  private discardOnSessionDestroy(session: FileChangesSessionService): void {
    if (this.trackedFileSessions.has(session)) {
      return;
    }
    this.trackedFileSessions.add(session);
    session.componentDestroyed$.pipe(
      take(1),
      switchMap(() => this.discardPendingFileChanges(session))
    ).subscribe();
  }
}

